import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { map } from 'rxjs/operators';

export interface User {
  _id: string;
  refNo?: string;
  title?: string;
  firstName: string;
  lastName: string;
  gender?: 'MALE' | 'FEMALE' | 'NON_BINARY';
  email: string;
  mobileNumber?: string;
  nationalInsuranceNo?: string;
  status: 'INVITED' | 'PENDING' | 'APPROVED' | 'DENIED' | 'DEACTIVATED' | 'LOCKED';
  isActive: boolean;
  isArchived: boolean;
  createdAt: string;
  accountSetupStep?: number;
  roleId?: {
    _id: string;
    name: string;
    identification: string;
  };
  subscriberId?: {
    _id: string;
    companyName: string;
  };
  address?: {
    streetAddress?: string;
    houseNumber?: string;
    postcode?: string;
    city?: string;
    country?: string;
  };
  citizenship?: string;
  taxNumber?: string;
  tradeLicence?: 'YES' | 'NO';
  idCard?: string;
  products?: Array<{_id: string, name: string, code: string} | string>;
  territories?: Array<{_id: string, name: string, region: string} | string>;
  termsAccepted?: boolean;
  language?: string;
}

export interface PagedUsersRequest {
  pageIndex: number;
  pageSize: number;
  sortField?: string;
  sortOrder?: 1 | -1;
  filters?: {
    subscriberId?: string;
    searchText?: string;
    status?: string;
    roleId?: string;
    archived?: boolean;
  };
}

export interface PagedUsersResponse {
  message: string;
  data: User[];
  metadata: Array<{
    total: number;
    page: number;
  }>;
}

export interface UserCountsByRole {
  [roleIdentification: string]: number;
}

export interface PendingApproval {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  status: string;
  createdAt: string;
  roleId?: {
    _id: string;
    name: string;
    identification: string;
  };
  subscriberId?: {
    _id: string;
    companyName: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  constructor(private http: HttpClient) {}

  // Get paged users with advanced filtering
  getPagedUsers(params: PagedUsersRequest): Observable<PagedUsersResponse> {
    return this.http.post<{message: string, data: User[], metadata: Array<{total: number, page: number}>}>(`${environment.apiUrl}/users/paged`, params).pipe(
      map(response => ({
        message: response.message,
        data: response.data || [],
        metadata: response.metadata || []
      }))
    );
  }

  // Get all users (legacy)
  getAllUsers(): Observable<User[]> {
    return this.http.get<{message: string, users: User[]}>(`${environment.apiUrl}/users`).pipe(
      map(response => response.users || [])
    );
  }

  // Get users by subscriber
  getUsersBySubscriber(subscriberId: string): Observable<User[]> {
    if (subscriberId === 'all') {
      return this.getAllUsers();
    }
    // Let the interceptor handle the subscriber ID via query parameter
    return this.http.get<{message: string, users: User[]}>(`${environment.apiUrl}/users?subscriberId=${subscriberId}`).pipe(
      map(response => response.users || [])
    );
  }

  getUsersBySubscriberId(subscriberId: string): Observable<User[]> {
    return this.http.get<{message: string, users: User[]}>(`${environment.apiUrl}/users/subscriber/${subscriberId}`).pipe(
      map(response => response.users || [])
    );
  }

  // Get user by ID
  getUserById(userId: string): Observable<User> {
    return this.http.get<{message: string, user: User}>(`${environment.apiUrl}/users/${userId}`).pipe(
      map(response => response.user)
    );
  }

  // Get pending approvals
  getPendingApprovals(): Observable<PendingApproval[]> {
    return this.http.get<{message: string, users: PendingApproval[]}>(`${environment.apiUrl}/users/pending-approvals`).pipe(
      map(response => response.users || [])
    );
  }

  // Get user counts by role
  getUserCountsByRole(roleIdentifications: string[], subscriberId?: string): Observable<UserCountsByRole> {
    let url = `${environment.apiUrl}/users/counts-by-role?roleIdentifications=${roleIdentifications.join(',')}`;
    if (subscriberId) {
      url += `&subscriberId=${subscriberId}`;
    }
    return this.http.get<{message: string, counts: UserCountsByRole}>(url).pipe(
      map(response => response.counts || {})
    );
  }

  // User actions
  approveUser(userId: string): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/users/${userId}/approve`, {});
  }

  denyUser(userId: string, reason?: string): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/users/${userId}/deny`, { reason });
  }

  lockUser(userId: string): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/users/${userId}/lock`, {});
  }

  unlockUser(userId: string): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/users/${userId}/unlock`, {});
  }

  deactivateUser(userId: string): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/users/${userId}/deactivate`, {});
  }

  activateUser(userId: string): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/users/${userId}/activate`, {});
  }

  archiveUser(userId: string): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/users/${userId}/archive`, {});
  }

  restoreUser(userId: string): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/users/${userId}/restore`, {});
  }

  resetUserPassword(userId: string): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/users/${userId}/password`, {});
  }

  generateAndSendNewPassword(userId: string): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/users/${userId}/generate-password`, {});
  }

  // Invite new user
  inviteUser(userData: {
    firstName: string;
    lastName: string;
    email: string;
    roleId: string;
    subscriberId?: string;
    companyName?: string;
  }): Observable<any> {
    return this.http.post(`${environment.apiUrl}/users/invite`, userData);
  }

  // Register new user (for sign-up)
  registerUser(userData: {
    firstName: string;
    lastName: string;
    email: string;
    roleId: string;
    companyName?: string;
    recaptchaToken?: string;
  }): Observable<any> {
    return this.http.post(`${environment.apiUrl}/users/register`, userData);
  }

  updateUser(userId: string, userData: {
    firstName: string;
    lastName: string;
    email: string;
    roleId: string;
    subscriberId?: string;
    companyName?: string;
  }): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/users/${userId}`, userData);
  }

  // Complete user registration
  completeUserRegistration(userData:any): Observable<any> {
    return this.http.post(`${environment.apiUrl}/users/register-complete`, userData);
  }

  // Get current user's profile
  getCurrentUserProfile(): Observable<User> {
    return this.http.get<{message: string, user: User}>(`${environment.apiUrl}/users/me?populate=territories,products`).pipe(
      map(response => response.user)
    );
  }

  // Update current user's profile
  updateCurrentUserProfile(userData: Partial<User>): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/users/me`, userData);
  }

  // Change current user's password
  changeCurrentUserPassword(passwordData: {
    currentPassword: string;
    newPassword: string;
  }): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/users/me/password`, passwordData);
  }

  // Change current user's password (First Time)
  changeCurrentUserPasswordFirstTime(passwordData: {
    newPassword: string;
  }): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/users/me/password/firstTime`, passwordData);
  }

  // Check profile completion status
  checkProfileCompletion(): Observable<any> {
    return this.http.get(`${environment.apiUrl}/users/me/profile-completion`);
  }

  // Mark first login as complete
  markFirstLoginComplete(): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/users/me/first-login-complete`, {});
  }

  // Accept terms and conditions
  acceptTermsAndConditions(): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/users/me/accept-terms`, {});
  }

  // Check subscription status
  checkSubscriptionStatus(): Observable<any> {
    return this.http.get(`${environment.apiUrl}/users/me/subscription-status`);
  }
}